import { useState, useEffect, useRef, useMemo } from 'react';
import abcjs from 'abcjs';
import { getUserProfile, getFollowing } from '../utils/socialFirestore';
import {
  tunePlainText, setPlainText, sessionPlainText, bulkPlainText,
  tunePrintHtml, setPrintHtml, sessionPrintHtml, bulkPrintHtml,
  shareOrDownloadText, openPrintWindow,
  slugify, formatExportDate,
} from '../utils/exportUtils';

/**
 * SendExportModal — unified Send / Export modal.
 *
 * Send props:  user, onSend(recipientUid, contact)
 * Export props: type ('tune'|'set'|'session'|'bulk'), item, tunes
 *
 * defaultTab: 'send' | 'export'  (defaults to 'send')
 */
export default function SendExportModal({
  open, onClose, title,
  // send
  user, onSend,
  // export
  type, item, tunes = [],
  defaultTab = 'send',
}) {
  const [tab, setTab] = useState(defaultTab);

  // ── Send state ───────────────────────────────────────────
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [sendStatus, setSendStatus] = useState('');

  useEffect(() => {
    if (!open || !user || tab !== 'send') return;
    setLoadingContacts(true);
    setSendStatus('');
    (async () => {
      const uids = await getFollowing(user.uid);
      const profiles = await Promise.all(
        uids.map(async (uid) => {
          const p = await getUserProfile(uid);
          return p ? { uid, ...p } : { uid, displayName: 'Unknown', username: '' };
        })
      );
      setContacts(profiles.filter(p => p.username !== 'allmytunes'));
      setLoadingContacts(false);
    })();
  }, [open, user, tab]);

  const handlePick = async (contact) => {
    if (sendStatus === 'sending') return;
    setSendStatus('sending');
    try {
      await onSend(contact.uid, contact);
      setSendStatus('sent');
      setTimeout(() => handleClose(), 1500);
    } catch {
      setSendStatus('error');
    }
  };

  // ── Export state ─────────────────────────────────────────
  const [exportFormat, setExportFormat] = useState('text');
  const [fullDetails, setFullDetails] = useState(true);
  const abcRenderRef = useRef(null);

  const resolvedTunes = useMemo(() => {
    if (type === 'tune') return item ? [item] : [];
    if (type === 'bulk') return tunes;
    if (type === 'session') return item?.tunes || [];
    if (type === 'set') {
      return (item?.tunes || []).map(ref => tunes.find(t => t.id === ref.id) || ref);
    }
    return [];
  }, [type, item, tunes]);

  const exportFilename = useMemo(() => {
    const date = new Date().toISOString().slice(0, 10);
    if (type === 'tune') return `${slugify(item?.title)}.txt`;
    if (type === 'set') return `set-${slugify(item?.title)}.txt`;
    if (type === 'session') {
      const d = item?.startedAt ? formatExportDate(item.startedAt).replace(/[^a-z0-9]+/gi, '-') : date;
      return `session-${slugify(item?.location)}-${d}.txt`;
    }
    return `allmytunes-export-${date}.txt`;
  }, [type, item]);

  const textContent = useMemo(() => {
    if (!open || tab !== 'export') return '';
    if (type === 'tune') return tunePlainText(item);
    if (type === 'set') return setPlainText(item, resolvedTunes, { fullDetails });
    if (type === 'session') return sessionPlainText(item, { fullDetails });
    if (type === 'bulk') return bulkPlainText(resolvedTunes);
    return '';
  }, [open, tab, type, item, resolvedTunes, fullDetails]);

  function buildSvgMap(tunesToRender) {
    const map = {};
    const container = abcRenderRef.current;
    if (!container) return map;
    for (const tune of tunesToRender) {
      if (!tune.abc || tune.mode === 'notes') continue;
      try {
        container.innerHTML = '';
        abcjs.renderAbc(container, tune.abc, { staffwidth: 560, add_classes: true });
        const svg = container.querySelector('svg');
        if (svg) {
          map[tune.id] = svg.outerHTML;
        }
      } catch { /* fall back to text */ }
    }
    container.innerHTML = '';
    return map;
  }

  const handlePrint = () => {
    const svgMap = buildSvgMap(resolvedTunes);
    let html;
    if (type === 'tune') html = tunePrintHtml(item, svgMap[item?.id] || null);
    else if (type === 'set') html = setPrintHtml(item, resolvedTunes, svgMap, { fullDetails });
    else if (type === 'session') html = sessionPrintHtml(item, svgMap, { fullDetails });
    else html = bulkPrintHtml(resolvedTunes, svgMap);
    openPrintWindow(html);
  };

  const handleShare = async () => {
    await shareOrDownloadText(textContent, exportFilename);
  };

  // ── Shared ───────────────────────────────────────────────
  const handleClose = () => {
    setSendStatus('');
    setExportFormat('text');
    onClose();
  };

  // Reset tab when defaultTab changes (e.g. swipe vs button)
  useEffect(() => { if (open) setTab(defaultTab); }, [open, defaultTab]);

  if (!open) return null;

  const showDetailToggle = type === 'set' || type === 'session' || type === 'bulk';

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-primary)', borderRadius: '12px', padding: '1.5rem',
          width: '100%', maxWidth: '380px', maxHeight: '85vh', overflowY: 'auto', position: 'relative',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.82rem', opacity: 0.6, marginTop: '2px', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
          <button className="btn-subtle" onClick={handleClose} style={{ padding: '4px 10px', fontSize: '1.1rem' }}>✕</button>
        </div>

        {/* Tab toggle */}
        <div style={{ display: 'flex', borderRadius: '8px', border: '1px solid var(--card-border, #ccc)', overflow: 'hidden', marginBottom: '1.25rem' }}>
          {[{ key: 'send', label: '📤 Send to User' }, { key: 'export', label: '📥 Export' }].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                flex: 1, padding: '7px', fontSize: '0.83rem',
                fontWeight: tab === t.key ? 700 : 400,
                backgroundColor: tab === t.key ? 'var(--bg-card)' : 'transparent',
                border: 'none', cursor: 'pointer', color: 'var(--text-primary)',
                borderRight: t.key === 'send' ? '1px solid var(--card-border, #ccc)' : 'none',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Send tab ── */}
        {tab === 'send' && (
          <>
            {loadingContacts ? (
              <p style={{ textAlign: 'center' }}>Loading contacts...</p>
            ) : contacts.length === 0 ? (
              <p style={{ textAlign: 'center', opacity: 0.7 }}>
                You're not following anyone yet. Follow musicians from the Social page to send them items.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {contacts.map(contact => (
                  <div
                    key={contact.uid}
                    onClick={() => handlePick(contact)}
                    style={{
                      padding: '10px 12px', backgroundColor: 'var(--input-bg)', borderRadius: '8px',
                      cursor: sendStatus === 'sending' ? 'default' : 'pointer',
                      display: 'flex', alignItems: 'center', gap: '10px',
                      opacity: sendStatus === 'sending' ? 0.6 : 1,
                    }}
                  >
                    {contact.photoURL && (
                      <img src={contact.photoURL} alt="" style={{ width: '36px', height: '36px', borderRadius: '50%' }} />
                    )}
                    <div>
                      <strong>{contact.displayName || 'Musician'}</strong>
                      {contact.username && (
                        <span style={{ opacity: 0.6, marginLeft: '6px', fontSize: '0.85em' }}>@{contact.username}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {sendStatus === 'sending' && <p style={{ textAlign: 'center', marginTop: '0.75rem' }}>Sending...</p>}
            {sendStatus === 'sent' && <p style={{ textAlign: 'center', marginTop: '0.75rem', color: '#2a2' }}>Sent!</p>}
            {sendStatus === 'error' && <p style={{ textAlign: 'center', marginTop: '0.75rem', color: '#e44' }}>Failed to send. Please try again.</p>}
          </>
        )}

        {/* ── Export tab ── */}
        {tab === 'export' && (
          <>
            {/* Format toggle */}
            <div style={{ display: 'flex', borderRadius: '8px', border: '1px solid var(--card-border, #ccc)', overflow: 'hidden', marginBottom: '1rem' }}>
              {[{ key: 'text', label: 'Plain Text' }, { key: 'print', label: 'Print / PDF' }].map(f => (
                <button
                  key={f.key}
                  onClick={() => setExportFormat(f.key)}
                  style={{
                    flex: 1, padding: '7px', fontSize: '0.83rem',
                    fontWeight: exportFormat === f.key ? 700 : 400,
                    backgroundColor: exportFormat === f.key ? 'var(--bg-card)' : 'transparent',
                    border: 'none', cursor: 'pointer', color: 'var(--text-primary)',
                    borderRight: f.key === 'text' ? '1px solid var(--card-border, #ccc)' : 'none',
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {showDetailToggle && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.88rem', marginBottom: '1rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={fullDetails} onChange={e => setFullDetails(e.target.checked)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                Include full details for each {type === 'session' || type === 'bulk' ? 'tune' : 'item'}
              </label>
            )}

            {exportFormat === 'text' && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.78rem', opacity: 0.5, marginBottom: '4px' }}>Preview</div>
                <pre style={{
                  fontSize: '0.72rem', lineHeight: 1.5, backgroundColor: 'var(--input-bg)',
                  borderRadius: '6px', padding: '0.6rem 0.75rem', maxHeight: '160px', overflowY: 'auto',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0,
                  color: 'var(--text-primary)', fontFamily: 'monospace',
                }}>
                  {textContent || '(nothing to export)'}
                </pre>
              </div>
            )}

            {exportFormat === 'print' && (
              <p style={{ fontSize: '0.8rem', opacity: 0.6, marginBottom: '1rem', lineHeight: 1.5 }}>
                Opens a formatted page with the print dialog. Choose <strong>Save as PDF</strong> or use the share icon.
                {(item?.abc || resolvedTunes.some(t => t.abc && t.mode !== 'notes')) ? ' ABC notation will be rendered as music.' : ''}
              </p>
            )}

            {exportFormat === 'text' ? (
              <button className="btn-primary" style={{ width: '100%', padding: '10px' }} onClick={handleShare} disabled={!textContent}>
                Share / Download .txt
              </button>
            ) : (
              <button className="btn-primary" style={{ width: '100%', padding: '10px' }} onClick={handlePrint}>
                Open Print Preview
              </button>
            )}
          </>
        )}

        {/* Off-screen div for abcjs rendering — needs real width so abcjs can reflow music */}
        <div ref={abcRenderRef} style={{ position: 'fixed', left: '-9999px', top: 0, width: '650px', visibility: 'hidden', pointerEvents: 'none' }} aria-hidden="true" />
      </div>
    </div>
  );
}
